/**
 * Copyright 2023 LiveKit, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  AudioTrack,
  LiveKitRoom,
  TrackReference,
  useRoomContext,
  useRoomInfo,
  useTracks,
} from '@livekit/components-react';
import EgressHelper from '@livekit/egress-sdk';
import { ConnectionState, RoomEvent, Track, VideoQuality } from 'livekit-client';
import type { RemoteTrackPublication, RoomOptions } from 'livekit-client';
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PrimaryHostLayout, { PrimaryHostDesktopLayout } from './PrimaryHostLayout';
import ScreenshareLayout from './ScreenshareLayout';
import SingleSpeakerLayout from './SingleSpeakerLayout';
import SpeakerLayout from './SpeakerLayout';
import { NameCardProvider } from './NameCardOverlay';
import ParticipantTileWithFallback from './ParticipantTileWithFallback';
import type { NameCardEntry, WatermarkPosition } from './common';
import { getPlaceholderUrl } from './url';
import { getDesktopLiveStreamSafeFramingScale } from './safeFraming';

// Recorder-specific room options. The egress browser is not a real viewer:
// the goal is to capture the *highest* fidelity copy of every track, not to
// adapt down based on the rendered <video> size or the (synthetic) bandwidth
// estimate of a headless Chromium. Without disabling adaptiveStream, LiveKit
// will silently downgrade subscribed tracks to a medium simulcast layer when
// it thinks the render target is small, which manifests as soft / blurry
// recordings. Each subscribed video publication is also pinned to HIGH below.
const recorderRoomOptions: RoomOptions = {
  adaptiveStream: false,
  dynacast: false,
};

interface RoomPageProps {
  url: string;
  token: string;
  layout: string;
  preview?: boolean;
}

interface TemplateState {
  layout: string;
  [key: string]: unknown;
}

export default function RoomPage({ url, token, layout, preview }: RoomPageProps) {
  const [error, setError] = useState<Error>();
  if (!url || !token) {
    return <div className="error">missing required params url and token</div>;
  }

  return (
    <LiveKitRoom serverUrl={url} token={token} options={recorderRoomOptions} onError={setError}>
      {error ? <div className="error">{error.message}</div> : <CompositeTemplate layout={layout} preview={preview} />}
    </LiveKitRoom>
  );
}

interface CompositeTemplateProps {
  layout: string;
  preview?: boolean;
}

function CompositeTemplate({ layout: initialLayout, preview }: CompositeTemplateProps) {
  const room = useRoomContext();
  const items = initialLayout.split('|');
  const [layout, setLayout] = useState(initialLayout);
  const [hasScreenShare, setHasScreenShare] = useState(false);
  const [soloUserId, setSoloUserId] = useState(items.length === 2 ? items[1] : '');
  const [safeFramingScale, setSafeFramingScale] = useState<number | null>(null);
  const [watermarkUrl, setWatermarkUrl] = useState<string | null>(null);
  const [watermarkPosition, setWatermarkPosition] = useState<WatermarkPosition>('top_left');
  const [watermarkLoaded, setWatermarkLoaded] = useState(false);
  const [hasNameCards, setHasNameCards] = useState(false);
  const pendingLayoutUpdate = useRef<ReturnType<typeof setTimeout> | null>(null);
  const screenshareTracks = useTracks([Track.Source.ScreenShare], {
    onlySubscribed: true,
  });
  const soloMode = soloUserId !== '' && layout.startsWith('single-speaker');
  const desktopMode = layout.startsWith('desktop');
  const roomContainerRef = useRef<HTMLDivElement>(null);
  const { metadata: roomMetadata } = useRoomInfo();
  const [highQualityEgress, setHighQualityEgress] = useState(false);

  useEffect(() => {
    if (!room || !preview) return;
    const logData = (payload: Uint8Array, _participant: unknown, _kind: unknown, topic?: string) => {
      console.log('[Room] DataReceived', {
        topic,
        size: payload.byteLength,
        text: new TextDecoder().decode(payload),
      });
    };
    room.on(RoomEvent.DataReceived, logData);
    return () => {
      room.off(RoomEvent.DataReceived, logData);
    };
  }, [room, preview]);

  // Force every subscribed video publication to deliver its highest simulcast
  // layer. Combined with adaptiveStream:false on the room, this ensures the
  // recorder always receives 1080p (or whatever the publisher's top layer is)
  // regardless of the rendered <video> element size or LiveKit's view of
  // available bandwidth in this headless browser.
  // Only active when high_quality_egress is set in room metadata (controlled
  // by the host's livekit_high_quality_egress site config).
  useEffect(() => {
    if (!room || !highQualityEgress) return;
    const pinHighQuality = (publication: RemoteTrackPublication) => {
      if (publication.kind !== Track.Kind.Video) return;
      try {
        publication.setVideoQuality(VideoQuality.HIGH);
      } catch (err) {
        console.warn('[Room] Failed to pin publication to HIGH quality', err);
      }
    };
    for (const participant of Array.from(room.remoteParticipants.values())) {
      for (const publication of Array.from(participant.trackPublications.values())) {
        pinHighQuality(publication as RemoteTrackPublication);
      }
    }
    const onTrackSubscribed = (_track: unknown, publication: RemoteTrackPublication) => pinHighQuality(publication);
    const onTrackPublished = (publication: RemoteTrackPublication) => pinHighQuality(publication);
    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);
    room.on(RoomEvent.TrackPublished, onTrackPublished);
    return () => {
      room.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
      room.off(RoomEvent.TrackPublished, onTrackPublished);
    };
  }, [room, highQualityEgress]);

  useEffect(() => {
    // preview=1 → skip EgressHelper so real egress workers don't get stuck.
    if (!room || preview) return;

    EgressHelper.setRoom(room);

    EgressHelper.onLayoutChanged((newLayout) => {
      const items = newLayout.split('|');
      if (items.length === 1) {
        setLayout(newLayout);
      } else {
        setLayout(items[0]);
        setSoloUserId(items[1]);
      }
    });

    let hasTrack = false;
    for (const p of Array.from(room.remoteParticipants.values())) {
      if (!soloUserId || (soloUserId && p.metadata && JSON.parse(p.metadata).user_id === soloUserId)) {
        if (p.trackPublications.size > 0) {
          hasTrack = true;
          break;
        }
      }
    }

    if (hasTrack) {
      EgressHelper.startRecording();
    } else {
      room.once(RoomEvent.TrackSubscribed, () => EgressHelper.startRecording());
    }
  }, [room, layout, soloUserId, preview]);

  // Parse safe framing config from room metadata using reactive hook
  useEffect(() => {
    if (!roomMetadata) return;
    try {
      const parsed = JSON.parse(roomMetadata) as {
        desktop_live_stream_safe_framing?: number;
        watermark_url?: string;
        watermark_position?: WatermarkPosition;
        name_cards?: { cards?: NameCardEntry[] };
        high_quality_egress?: boolean;
      };
      setSafeFramingScale(getDesktopLiveStreamSafeFramingScale(parsed.desktop_live_stream_safe_framing));
      setWatermarkUrl(parsed.watermark_url ?? null);
      setWatermarkPosition(parsed.watermark_position ?? 'top_left');
      setHasNameCards((parsed.name_cards?.cards?.length ?? 0) > 0);
      setHighQualityEgress(!!parsed.high_quality_egress);
    } catch {
      // ignore malformed metadata
    }
  }, [roomMetadata]);

  useEffect(() => {
    if (!desktopMode || !watermarkUrl) {
      setWatermarkLoaded(false);
      return;
    }
    const img = new Image();
    img.onload = () => setWatermarkLoaded(true);
    img.onerror = () => setWatermarkLoaded(false);
    img.src = watermarkUrl;
  }, [desktopMode, watermarkUrl]);

  useEffect(() => {
    if (screenshareTracks.length > 0 && screenshareTracks[0].publication) {
      setHasScreenShare(true);
    } else {
      setHasScreenShare(false);
    }
  }, [screenshareTracks]);

  const allTracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare, Track.Source.Unknown], {
    onlySubscribed: true,
  });
  const audioTracks = useTracks([Track.Source.Microphone], {
    onlySubscribed: true,
  });

  const isAdminParticipant = useCallback((metadata: string | undefined): boolean => {
    if (!metadata) return false;
    try {
      return JSON.parse(metadata).isAdmin === true;
    } catch {
      return false;
    }
  }, []);

  const filteredTracks = useMemo(() => {
    return allTracks.filter(
      (tr) =>
        tr.publication.kind === Track.Kind.Video &&
        !isAdminParticipant(tr.participant.metadata) &&
        (!soloMode || tr.source !== Track.Source.ScreenShare) &&
        (!soloUserId ||
          (soloUserId &&
            tr.participant.metadata &&
            String(JSON.parse(tr.participant.metadata).user_id) === soloUserId)) &&
        tr.participant.identity !== room.localParticipant.identity
    );
  }, [allTracks, soloUserId, room.localParticipant.identity, isAdminParticipant]);

  const filteredAudioTracks = useMemo(() => {
    return audioTracks.filter(
      (track) =>
        !isAdminParticipant(track.participant.metadata) && track.participant.identity !== room.localParticipant.identity
    );
  }, [audioTracks, isAdminParticipant, room.localParticipant.identity]);

  // Screenshare audio is tracked separately from mic audio so it can be
  // rendered in both soloMode and non-soloMode without interfering with the
  // soloAudioMicReference lookup (which expects mic-only tracks).
  const screenShareAudioTracks = useTracks([Track.Source.ScreenShareAudio], {
    onlySubscribed: true,
  });
  const filteredScreenShareAudioTracks = useMemo(() => {
    return screenShareAudioTracks.filter(
      (track) =>
        !isAdminParticipant(track.participant.metadata) &&
        track.participant.identity !== room.localParticipant.identity &&
        (!soloMode ||
          (track.participant.metadata && String(JSON.parse(track.participant.metadata).user_id) === soloUserId))
    );
  }, [screenShareAudioTracks, isAdminParticipant, room.localParticipant.identity, soloMode, soloUserId]);

  const audioTracksWithoutVideo = useMemo(() => {
    return filteredAudioTracks.filter((track) => {
      return !filteredTracks.find(
        (ft) =>
          ft.participant.identity === track.participant.identity &&
          ft.participant.identity !== room.localParticipant.identity
      );
    });
  }, [filteredAudioTracks, filteredTracks, room.localParticipant.identity]);

  const combinedTracks: TrackReference[] = useMemo(() => {
    return [...filteredTracks, ...audioTracksWithoutVideo];
  }, [filteredTracks, audioTracksWithoutVideo]);

  const sortedTracks = useMemo(() => {
    return [...combinedTracks].sort((a, b) => {
      // sort by host first
      const isAHost = a.participant.metadata ? JSON.parse(a.participant.metadata)['isHost'] === true : false;
      const isBHost = b.participant.metadata ? JSON.parse(b.participant.metadata)['isHost'] === true : false;
      if (isAHost && !isBHost) {
        return -1;
      } else if (!isAHost && isBHost) {
        return 1;
      }
      return Number(a.participant.identity) - Number(b.participant.identity);
    });
  }, [combinedTracks]);

  const soloAudioMicReference = audioTracks.find(
    (tr) => soloUserId && sortedTracks.some((t) => t.participant.identity === tr.participant.identity)
  );

  let interfaceStyle = 'dark';
  if (layout.endsWith('-light')) {
    interfaceStyle = 'light';
  }

  let containerClass = 'roomContainer';
  if (interfaceStyle) {
    containerClass += ` ${interfaceStyle}`;
  }

  // determine layout to use
  let main: ReactElement = <></>;
  let effectiveLayout = layout;
  if (hasScreenShare && layout.startsWith('grid')) {
    effectiveLayout = layout.replace('grid', 'speaker');
  }

  const updateEgressLayout = useCallback(
    (nextLayout: string) => {
      if (!room || !room.localParticipant) {
        return;
      }

      const currentMetadata = room.localParticipant.metadata;
      let nextMetadata: TemplateState = { layout: nextLayout };

      if (currentMetadata) {
        try {
          const parsed = JSON.parse(currentMetadata) as TemplateState;
          if (parsed.layout === nextLayout) {
            return;
          }
          nextMetadata = { ...parsed, layout: nextLayout };
        } catch (err) {
          console.error('Failed to parse recorder metadata, overwriting layout', err);
        }
      }

      if (pendingLayoutUpdate.current) {
        clearTimeout(pendingLayoutUpdate.current);
      }

      pendingLayoutUpdate.current = setTimeout(() => {
        room.localParticipant.setMetadata(JSON.stringify(nextMetadata)).catch((err) => {
          console.error('Failed to update egress layout metadata', err);
        });
      }, 150);
    },
    [room]
  );

  if (!effectiveLayout.startsWith('speaker') && !effectiveLayout.startsWith('all') && sortedTracks.length > 2) {
    effectiveLayout = 'primary-host';
  }

  useEffect(() => {
    updateEgressLayout(effectiveLayout);
  }, [effectiveLayout, updateEgressLayout]);

  useEffect(() => {
    return () => {
      if (pendingLayoutUpdate.current) {
        clearTimeout(pendingLayoutUpdate.current);
      }
    };
  }, []);

  useEffect(() => {
    sortedTracks.forEach((track, index) => {
      if (effectiveLayout !== 'primary-host' && track.participant.identity) {
        document.documentElement.style.setProperty(
          `--participant-background-image-${index + 1}`,
          getPlaceholderUrl(track.participant.identity)
        );
      }
    });
  }, [sortedTracks, effectiveLayout]);

  const sortedTracksForAllLayout = useMemo(() => {
    return [...sortedTracks].sort((a, b) => Number(a.participant.identity) - Number(b.participant.identity));
  }, [sortedTracks]);

  if (room.state !== ConnectionState.Disconnected) {
    // When screenshare is active, use the dedicated ScreenshareLayout
    if (hasScreenShare && screenshareTracks.length > 0 && !soloMode) {
      const cameraTracks = sortedTracks.filter((t) => t.source !== Track.Source.ScreenShare);
      main = <ScreenshareLayout screenshareTrack={screenshareTracks[0]} cameraTracks={cameraTracks} />;
    } else if (desktopMode) {
      if (effectiveLayout.startsWith('primary-host')) {
        main = <PrimaryHostDesktopLayout tracks={sortedTracks} safeFramingScale={safeFramingScale} />;
      } else {
        main = (
          <div className={'desktop-layout-' + sortedTracks.length}>
            {sortedTracks.map((track, index) => {
              const clientPlatform = track.participant.metadata
                ? (JSON.parse(track.participant.metadata) as { client_platform?: string }).client_platform
                : null;
              const safeFramingScaleForTrack = clientPlatform === 'web' ? safeFramingScale : null;
              return (
                <ParticipantTileWithFallback key={index} trackRef={track} safeFramingScale={safeFramingScaleForTrack} />
              );
            })}
          </div>
        );
      }
    } else if (effectiveLayout.startsWith('all')) {
      main = (
        <div className="all-layout">
          {sortedTracksForAllLayout.map((track, index) => (
            <ParticipantTileWithFallback key={index} trackRef={track} />
          ))}
        </div>
      );
    } else if (effectiveLayout.startsWith('speaker')) {
      main = <SpeakerLayout tracks={sortedTracks} />;
    } else if (effectiveLayout.startsWith('single-speaker')) {
      main = <SingleSpeakerLayout tracks={sortedTracks} />;
    } else if (effectiveLayout.startsWith('primary-host')) {
      main = <PrimaryHostLayout tracks={sortedTracks} />;
    } else {
      main = (
        <div className="lk-grid-layout">
          {sortedTracks.map((track, index) => (
            <ParticipantTileWithFallback key={index} trackRef={track} />
          ))}
        </div>
      );
    }
  }

  // Name cards are desktop-only. Portrait composites (layout: 'grid') and
  // single-speaker per-participant egress renders skip the provider so no card
  // overlay is mounted.
  const nameCardsRoomMetadata = desktopMode ? roomMetadata : undefined;
  const hasBottomLeftWatermark = !!watermarkUrl && watermarkPosition === 'bottom_left';

  return (
    <NameCardProvider roomMetadata={nameCardsRoomMetadata} hasBottomLeftWatermark={hasBottomLeftWatermark}>
      <div className={containerClass} ref={roomContainerRef}>
        {main}
        {desktopMode && watermarkUrl && watermarkLoaded && (
          <img
            src={watermarkUrl}
            alt=""
            className={`watermark watermark-${watermarkPosition}${hasNameCards ? ' watermark--with-name-cards' : ''}`}
          />
        )}
        {/* when soloMode is true we never want to hear the room audio */}
        {soloMode
          ? soloAudioMicReference && <AudioTrack trackRef={soloAudioMicReference} />
          : filteredAudioTracks.map((track) => <AudioTrack key={track.participant.identity} trackRef={track} />)}
        {filteredScreenShareAudioTracks.map((track) => (
          <AudioTrack key={`screenshare-audio-${track.participant.identity}`} trackRef={track} />
        ))}
      </div>
    </NameCardProvider>
  );
}
